/**
 * Season and Race Selector Component
 *
 * A shared component for selecting a season (year) and a specific race.
 * Used across F1 Fantasy pages to provide consistent data filtering.
 *
 * Features:
 * - Season dropdown (filtered to available fantasy data years)
 * - Race dropdown (defaults to last race of the season)
 * - Races shown in reverse order (most recent first)
 * - Loading states
 */

import { Calendar, ChevronDown, Flag, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";
import { type Race, useRaces, useSeasons } from "../hooks/use-f1-data";
import { DEFAULT_SEASON } from "../lib/constants";
import { getNextOrLatestRace } from "../lib/race-utils";

// ============================================================================
// Types
// ============================================================================

export interface SeasonRaceSelectorProps {
  /** Callback when season changes */
  onSeasonChange?: (season: number) => void;
  /** Callback when race changes */
  onRaceChange?: (race: Race) => void;
  /** Default season if not in URL */
  defaultSeason?: number;
  /** Initial race slug from SSR loader data - if provided, will select this race instead of auto-selecting last race */
  initialRaceSlug?: string | null;
  /** Whether to show the race selector */
  showRaceSelector?: boolean;
  /** Custom class name */
  className?: string;
  /** Filter to only show specific seasons (e.g., [2023, 2024, 2025] for fantasy data) */
  filterSeasons?: number[];
  /** Compact mode: buttons fill parent grid cells (w-full, no min-width) */
  compact?: boolean;
  /** Only show past races + the next upcoming race (hides future races with no data) */
  filterRacesToSelectable?: boolean;
}

export interface SeasonRaceSelectorState {
  season: number;
  race: Race | null;
  raceRound: number | null;
}

// ============================================================================
// Component
// ============================================================================

export function SeasonRaceSelector({
  onSeasonChange,
  onRaceChange,
  defaultSeason,
  initialRaceSlug,
  showRaceSelector = true,
  className = "",
  filterSeasons,
  compact = false,
  filterRacesToSelectable = false,
}: SeasonRaceSelectorProps) {
  // Fetch available seasons
  const { data: allSeasons, isLoading: seasonsLoading } = useSeasons();

  // Filter seasons if filterSeasons prop is provided
  const seasons = filterSeasons
    ? allSeasons?.filter((s) => filterSeasons.includes(s.year))
    : allSeasons;

  // Determine initial season - use the most recent available season
  const initialSeason = useMemo(() => {
    if (defaultSeason) return defaultSeason;
    if (seasons && seasons.length > 0) return seasons[0].year;
    return DEFAULT_SEASON; // Fallback
  }, [defaultSeason, seasons]);

  // Local state for selected season and race
  const [selectedSeason, setSelectedSeason] = useState<number>(initialSeason);
  const [selectedRace, setSelectedRace] = useState<Race | null>(null);
  const [hasInitializedRace, setHasInitializedRace] = useState(false);

  // Dropdown open states
  const [seasonDropdownOpen, setSeasonDropdownOpen] = useState(false);
  const [raceDropdownOpen, setRaceDropdownOpen] = useState(false);

  // Fetch races for selected season
  const { data: races, isLoading: racesLoading } = useRaces(selectedSeason);

  // Capture current time once at mount — a render-time new Date() produces
  // different values on server vs client and can flip the "next race" filter
  // across a race-date boundary, causing a hydration mismatch (React #418).
  const [mountTime] = useState(() => Date.now());

  // When filterRacesToSelectable is true, show races that have fantasy data
  // plus the next upcoming race (even if it has no data yet — the server will
  // fetch on-demand when the user selects it, with a loading state).
  const selectableRaces = useMemo(() => {
    if (!races) return [];
    if (!filterRacesToSelectable) return races;
    const withData = races.filter((r) => r.hasFantasyData);
    // Also include the first upcoming race without data (the "next" race)
    const now = new Date(mountTime);
    const nextWithoutData = races.find(
      (r) => !r.hasFantasyData && r.date != null && new Date(r.date) > now,
    );
    if (nextWithoutData) withData.push(nextWithoutData);
    return withData;
  }, [races, filterRacesToSelectable, mountTime]);

  // Races in reverse order (most recent first)
  const racesReversed = useMemo(() => {
    return [...selectableRaces].reverse();
  }, [selectableRaces]);

  // Validate season during render — if selected season isn't in the available
  // list, correct it immediately (React-approved render-time state adjustment)
  if (
    seasons &&
    seasons.length > 0 &&
    !seasons.some((s) => s.year === selectedSeason)
  ) {
    setSelectedSeason(seasons[0].year);
  }

  // Auto-select race during render when races load and no race initialized yet
  if (selectableRaces.length > 0 && !hasInitializedRace) {
    let raceToSelect: Race | null = null;
    if (initialRaceSlug) {
      // Only select if the race is actually selectable (e.g. not a future race in a new season)
      raceToSelect =
        selectableRaces.find((r) => r.slug === initialRaceSlug) ?? null;
    }
    if (!raceToSelect) {
      raceToSelect = getNextOrLatestRace(selectableRaces);
    }
    setSelectedRace(raceToSelect);
    setHasInitializedRace(true);
  }

  // Notify parent callbacks when values change (no cascading setState —
  // these effects only call parent callbacks, not this component's setState)
  const prevSeasonRef = useRef(selectedSeason);
  const prevRaceRef = useRef<Race | null>(selectedRace);

  useEffect(() => {
    if (selectedSeason !== prevSeasonRef.current) {
      prevSeasonRef.current = selectedSeason;
      onSeasonChange?.(selectedSeason);
    }
  }, [selectedSeason, onSeasonChange]);

  useEffect(() => {
    if (selectedRace !== prevRaceRef.current) {
      prevRaceRef.current = selectedRace;
      if (selectedRace) {
        onRaceChange?.(selectedRace);
      }
    }
  }, [selectedRace, onRaceChange]);

  // Handle season change
  const handleSeasonChange = useCallback((year: number) => {
    setSelectedSeason(year);
    setSelectedRace(null);
    setHasInitializedRace(false);
    setSeasonDropdownOpen(false);
  }, []);

  // Handle race change
  const handleRaceChange = useCallback((race: Race) => {
    setSelectedRace(race);
    setRaceDropdownOpen(false);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-dropdown]")) {
        setSeasonDropdownOpen(false);
        setRaceDropdownOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Lock body scroll when a dropdown is open (prevents page scroll on mobile)
  useEffect(() => {
    const isOpen = seasonDropdownOpen || raceDropdownOpen;
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [seasonDropdownOpen, raceDropdownOpen]);

  return (
    <div
      className={
        compact
          ? `flex items-center gap-2 ${className}`
          : `flex items-center gap-2 md:gap-3 ${className}`
      }
    >
      {/* Season Selector */}
      <div className={`relative ${compact ? "shrink-0" : ""}`} data-dropdown>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setSeasonDropdownOpen(!seasonDropdownOpen);
            setRaceDropdownOpen(false);
          }}
          className={`flex items-center gap-1.5 py-2 rounded-lg bg-card border border-border hover:border-primary/50 transition-colors text-sm ${
            compact ? "px-2.5" : "md:gap-2 px-2.5 md:px-4 md:text-base"
          }`}
          disabled={seasonsLoading}
        >
          <Calendar className="h-4 w-4 text-muted-foreground" />
          {seasonsLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <span className="font-medium text-foreground">
              {selectedSeason}
            </span>
          )}
          <ChevronDown
            className={`h-4 w-4 text-muted-foreground ml-auto transition-transform ${
              seasonDropdownOpen ? "rotate-180" : ""
            }`}
          />
        </button>

        {seasonDropdownOpen && seasons && (
          <div className="absolute top-full left-0 mt-2 w-full min-w-[120px] rounded-lg bg-card border border-border shadow-lg z-50 max-h-[400px] overflow-y-auto overscroll-contain">
            {seasons.map((season) => (
              <button
                key={season.id}
                type="button"
                onClick={() => handleSeasonChange(season.year)}
                className={`w-full px-4 py-2 text-left hover:bg-muted/50 transition-colors ${
                  season.year === selectedSeason
                    ? "bg-primary/10 text-primary"
                    : "text-foreground"
                }`}
              >
                <span className="font-medium">{season.year}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Race Selector */}
      {showRaceSelector && (
        <div
          className={`relative ${compact ? "flex-1 min-w-0" : ""}`}
          data-dropdown
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setRaceDropdownOpen(!raceDropdownOpen);
              setSeasonDropdownOpen(false);
            }}
            className={`flex items-center gap-1.5 py-2 rounded-lg bg-card border border-border hover:border-primary/50 transition-colors text-sm ${
              compact
                ? "w-full px-2.5"
                : "md:gap-2 px-2.5 md:px-4 min-w-0 max-w-[160px] md:max-w-none md:min-w-[180px] md:text-base"
            }`}
            disabled={racesLoading}
          >
            <Flag className="h-4 w-4 text-muted-foreground" />
            {racesLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <span className="font-medium text-foreground truncate">
                {selectedRace ? selectedRace.name : m.f1_selector_select_race()}
              </span>
            )}
            <ChevronDown
              className={`h-4 w-4 text-muted-foreground ml-auto transition-transform ${
                raceDropdownOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {raceDropdownOpen && racesReversed.length > 0 && (
            <div className="absolute top-full left-0 mt-2 w-full min-w-[220px] max-w-[calc(100vw-2rem)] max-h-[400px] overflow-y-auto overscroll-contain rounded-lg bg-card border border-border shadow-lg z-50">
              {/* Individual races - shown in reverse order (most recent first) */}
              {racesReversed.map((race) => (
                <button
                  key={race.id}
                  type="button"
                  onClick={() => handleRaceChange(race)}
                  className={`w-full px-4 py-2 text-left hover:bg-muted/50 transition-colors ${
                    selectedRace?.id === race.id
                      ? "bg-primary/10 text-primary"
                      : "text-foreground"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-muted-foreground w-6">
                      R{race.round}
                    </span>
                    <span className="font-medium">{race.name}</span>
                  </div>
                  {race.date && (
                    <div className="text-xs text-muted-foreground ml-8">
                      {new Date(race.date).toLocaleDateString(getLocale(), {
                        month: "short",
                        day: "numeric",
                      })}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Loading indicator for race data */}
      {showRaceSelector && racesLoading && (
        <span className="text-xs text-muted-foreground">
          {m.f1_loading_races()}
        </span>
      )}
    </div>
  );
}

// ============================================================================
// Hook for using selector state
// ============================================================================

/**
 * Hook to manage season/race selection state
 * Can be used independently of the component for custom UIs
 */
export function useSeasonRaceState(defaultSeason?: number) {
  const currentYear = new Date().getFullYear();
  const [season, setSeason] = useState<number>(defaultSeason ?? currentYear);
  const [race, setRace] = useState<Race | null>(null);

  const { data: seasons } = useSeasons();
  const { data: races } = useRaces(season);

  // Get the race round (null for all races)
  const raceRound = race?.round ?? null;

  return {
    season,
    setSeason,
    race,
    setRace,
    raceRound,
    seasons,
    races,
  };
}
